"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AutoType = exports.createAutoDefinition = exports.NewType = void 0;
const _internals_1 = require("../_internals");
const symbols_1 = require("../_internals/symbols");
const show_1 = __importDefault(require("./show"));
const mapObj = (fn) => (obj) => (0, _internals_1.fromPairs)((0, _internals_1.toPairs)(obj).map(fn));
const Box = (config) => (cases) => {
    Object.keys(cases).forEach((trivial, _, keys) => {
        cases[trivial].prototype.unwrap = function () {
            let inner = this.get();
            while (inner === null || inner === void 0 ? void 0 : inner.get) {
                inner = inner.get();
            }
            return inner;
        };
        cases[trivial].prototype.get = function () {
            return (0, symbols_1.getInnerValue)(this);
        };
        cases[trivial].prototype.match = function (patterns) {
            return (0, _internals_1.extractWith)([this.get()])((0, _internals_1.getCase)((0, symbols_1.getVariant)(this), (0, _internals_1.expandCases)(patterns)));
        };
        cases[trivial].prototype.tagMatch = function (patterns) {
            return (0, _internals_1.extractWith)([this.get()])((0, _internals_1.getCase)((0, symbols_1.getVariant)(this), patterns));
        };
        function callGet() { return this.get(); }
        function extractWithGet(fn) { return (0, _internals_1.extractWith)([this.get()])(fn); }
        function True() { return true; }
        function False() { return false; }
        if (!(config === null || config === void 0 ? void 0 : config.noHelpers) && Object.keys(cases).length > 1) {
            cases[trivial].prototype[`on${trivial}`] = extractWithGet;
            cases[trivial].prototype[`is${trivial}`] = True;
            keys.filter(x => x !== trivial).forEach(key => {
                cases[trivial].prototype[`is${key}`] = False;
                cases[trivial].prototype[`on${key}`] = callGet;
            });
        }
    });
};
const Pipeable = () => (cases) => {
    Object.keys(cases).forEach((trivial) => {
        cases[trivial].prototype.pipe = function pipe(fns) {
            return fns(this);
        };
        cases[trivial].prototype['|>'] = function pipe(fns) {
            return fns(this);
        };
    });
};
const Union = (name, cases, exts, config) => {
    const extensions = [Box(config), Pipeable(), ...exts];
    const tcs = extensions.map(tc => (0, symbols_1.getTypeclass)(tc)).filter(Boolean);
    let typeRep = {};
    const mappedCases = mapObj(([key, val]) => {
        const Case = function (...args) {
            (0, symbols_1.setTypeName)(name)(this);
            (0, symbols_1.setVariant)(key)(this);
            (0, symbols_1.setInnerValue)(val(...args))(this);
            (0, symbols_1.setTypeclasses)(() => tcs)(this);
            (0, symbols_1.setTypeRep)(() => typeRep)(this);
        };
        return [key, Case];
    })(cases);
    const globals = {};
    extensions.forEach(fn => fn(mappedCases, globals));
    return {
        constructors(cons) {
            const trueCases = mapObj(([key, value]) => {
                const Case = (...args) => new value(...args);
                (0, symbols_1.setVariant)(key)(Case);
                (0, symbols_1.setTypeRep)(() => typeRep)(Case);
                (0, symbols_1.setCaseHasInstance)(Case);
                return [key, Case];
            })(mappedCases);
            typeRep = Object.assign(Object.assign(Object.assign({}, trueCases), globals), mapObj(([key, fn]) => [key, fn.bind(trueCases)])(cons));
            (0, symbols_1.setTypeclasses)(() => tcs)(typeRep);
            (0, symbols_1.setRepHasInstance)(typeRep);
            return typeRep;
        }
    };
};
const NewType = (name, exts = []) => Union(name, { [name]: x => x }, [
    (0, show_1.default)({ overrides: {
            show: {
                [name]() {
                    return `[${name} => ${name} ${this.get()}]`;
                }
            }
        }
    }),
    ...exts,
]).constructors({
    of(...args) { return this[name](...args); },
    from(...args) { return this[name](...args); }
});
exports.NewType = NewType;
const createAutoDefinition = (name) => ({
    trivials: [name],
    pure: [name],
    resolve: [name],
    order: [name],
    first: name,
    config: {
        noHelpers: true,
    },
    overrides: {
        fold: {
            [name](fn) { return fn(this.get()); }
        }
    }
});
exports.createAutoDefinition = createAutoDefinition;
const AutoType = (name, exts = []) => {
    const autoDef = (0, exports.createAutoDefinition)(name);
    const filledDefs = exts.map(ext => ext(autoDef));
    return (0, exports.NewType)(name, filledDefs);
};
exports.AutoType = AutoType;
exports.default = Union;
